import type { SearchModuleRecord } from '../types'

let cached: Promise<SearchModuleRecord[]> | null = null

const conceptGroups = [
  ['spousal', 'spouse', 'partner', 'married'],
  ['benefits', 'benefit', 'social security', 'ss'],
  ['special needs', 'disabled child', 'disability', 'snt'],
  ['estate', 'inheritance', 'legacy', 'will', 'trust'],
  ['retirement', 'retire', 'fire', 'financial independence'],
  ['bond', 'bonds', 'fixed income', 'treasury', 'treasuries'],
  ['stock', 'stocks', 'equity', 'equities', 'shares'],
  ['tax', 'taxes', 'taxation', 'irs'],
  ['emergency fund', 'cash reserve', 'rainy day'],
  ['advisor', 'adviser', 'financial planner', 'fiduciary'],
]

export interface SearchResult {
  moduleId: string
  moduleTitle: string
  courseId: string
  courseTitle: string
  sectionId: string
  sectionLabel: string
  heading: string
  preview: string
  score: number
  url: string
}

export function normalise(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index)
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i]
    for (let j = 1; j <= b.length; j += 1) {
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      )
    }
    previous = current
  }
  return previous[b.length]
}

function expandQuery(query: string): string[] {
  const value = normalise(query)
  const expanded = new Set(value.split(' ').filter(Boolean))
  for (const group of conceptGroups) {
    if (group.some((term) => value.includes(normalise(term)))) {
      for (const term of group) for (const token of normalise(term).split(' ')) expanded.add(token)
    }
  }
  return [...expanded]
}

function tokenScore(query: string, candidate: string): number {
  if (query === candidate) return 12
  if (candidate.startsWith(query) || query.startsWith(candidate)) return 8
  if (candidate.includes(query) || query.includes(candidate)) return 5
  const maxDistance = query.length >= 8 ? 2 : query.length >= 5 ? 1 : 0
  if (maxDistance && levenshtein(query, candidate) <= maxDistance) return 7
  return 0
}

function scoreText(queryTokens: string[], text: string, weight: number): number {
  const candidateTokens = normalise(text).split(' ').filter(Boolean)
  let score = 0
  for (const query of queryTokens) {
    let best = 0
    for (const candidate of candidateTokens) best = Math.max(best, tokenScore(query, candidate))
    score += best * weight
  }
  return score
}

export async function loadSearchIndex(): Promise<SearchModuleRecord[]> {
  if (!cached) {
    cached = fetch('/course-search.json')
      .then(async (response) => {
        if (!response.ok) throw new Error('Course search index could not be loaded.')
        return response.json() as Promise<SearchModuleRecord[]>
      })
      .catch((error) => {
        cached = null
        throw error
      })
  }
  return cached
}

export function searchRecords(records: SearchModuleRecord[], query: string, limit = 30): SearchResult[] {
  const clean = normalise(query)
  if (!clean) return []
  const tokens = expandQuery(clean)
  const results: SearchResult[] = []
  for (const module of records) {
    const moduleScore =
      scoreText(tokens, module.id, 10)
      + scoreText(tokens, module.title, 7)
      + scoreText(tokens, module.summary, 3)
      + scoreText(tokens, module.courseTitle, 2)
    for (const section of module.sections) {
      let score = moduleScore
      score += scoreText(tokens, section.label, 6)
      score += scoreText(tokens, section.heading, 5)
      score += scoreText(tokens, section.text.slice(0, 8_000), 1)
      const haystack = normalise(`${module.title} ${module.summary} ${section.label} ${section.heading} ${section.text}`)
      if (haystack.includes(clean)) score += 30
      const matchedTokens = tokens.filter((token) => haystack.split(' ').some((candidate) => tokenScore(token, candidate) > 0))
      if (matchedTokens.length === tokens.length) score += 20
      if (score > 0) {
        results.push({
          moduleId: module.id,
          moduleTitle: module.title,
          courseId: module.courseId,
          courseTitle: module.courseTitle,
          sectionId: section.sectionId,
          sectionLabel: section.label,
          heading: section.heading,
          preview: section.preview,
          score,
          url: `/modules/${module.id}#${section.sectionId}`,
        })
      }
    }
  }
  return results
    .sort((a, b) => b.score - a.score || a.moduleId.localeCompare(b.moduleId) || a.sectionLabel.localeCompare(b.sectionLabel))
    .filter((result, index, all) => all.findIndex((item) => item.moduleId === result.moduleId && item.sectionId === result.sectionId) === index)
    .slice(0, limit)
}

export function searchConfidence(results: SearchResult[]): 'high' | 'low' | 'none' {
  if (!results.length) return 'none'
  if (results[0].score >= 80 || (results.length > 1 && results[0].score >= results[1].score * 1.4)) return 'high'
  return 'low'
}
